import type {DashboardSidebar} from '@github-ui/shared-dashboard-sidebar/DashboardSidebar'
import type {ComponentProps} from 'react'

import {
  generateRepoPullsBasePath,
  generateRepoPullsCleanPath,
  getRepoPullsCleanPath,
  type RepoPullsCleanPathSidebarLinkId,
  REPO_PULLS_CLEAN_PATHS,
  REPO_PULLS_DEFAULT_QUERY,
} from './repo-pulls-dashboard-content-route'

type DashboardSidebarLink = ComponentProps<typeof DashboardSidebar>['links'][number]
type RepoPullsSidebarLinkId = 'all' | RepoPullsCleanPathSidebarLinkId
type RepoPullsSidebarLink = DashboardSidebarLink & {id: RepoPullsSidebarLinkId; path: string}

interface RepoPullsSidebarLinksParams {
  currentUserLogin?: string
  owner: string
  repo: string
}

const SIDEBAR_LINK_PRESENTATION = {
  authored: {name: 'Authored by me', icon: 'smiley'},
  assigned: {name: 'Assigned to me', icon: 'person'},
  involves: {name: 'Involves me', icon: 'comment'},
  'review-requested': {name: 'Review requests', icon: 'git_pull_request'},
} as const satisfies Record<RepoPullsCleanPathSidebarLinkId, Pick<RepoPullsSidebarLink, 'name' | 'icon'>>

export function getRepoPullsSidebarLinks({
  currentUserLogin,
  owner,
  repo,
}: RepoPullsSidebarLinksParams): RepoPullsSidebarLink[] {
  const allPullsLink = {
    id: 'all',
    path: generateRepoPullsBasePath({owner, repo}),
    name: 'Pull requests',
    searchType: 'pull-requests',
    query: REPO_PULLS_DEFAULT_QUERY,
    icon: 'inbox',
    color: 'gray',
    source: 'predefined',
    preload: 'intent',
  } satisfies RepoPullsSidebarLink

  if (!currentUserLogin) return [allPullsLink]

  const personalLinks = REPO_PULLS_CLEAN_PATHS.filter(config => config.canonical).map(config => ({
    id: config.sidebarLinkId,
    path: generateRepoPullsCleanPath(config, {owner, repo, value: currentUserLogin}),
    name: SIDEBAR_LINK_PRESENTATION[config.sidebarLinkId].name,
    searchType: 'pull-requests' as const,
    query: `${REPO_PULLS_DEFAULT_QUERY} ${config.qualifier}:${currentUserLogin}`,
    icon: SIDEBAR_LINK_PRESENTATION[config.sidebarLinkId].icon,
    color: 'gray' as const,
    source: 'predefined' as const,
    preload: 'intent' as const,
  }))

  return [allPullsLink, ...personalLinks]
}

export type RepoPullsAnalyticsView = RepoPullsSidebarLinkId | 'custom'

export function normalizeRepoPullsQuery(query: string) {
  return query.trim().split(/\s+/).filter(Boolean).join(' ')
}

function getRepoPullsSidebarLinkForQuery(query: string, links: RepoPullsSidebarLink[]) {
  const normalizedQuery = normalizeRepoPullsQuery(query)
  return links.find(link => normalizeRepoPullsQuery(link.query) === normalizedQuery)
}

function isCanonicalRepoPullsCleanPathQuery(
  query: string,
  cleanPath: NonNullable<ReturnType<typeof getRepoPullsCleanPath>>,
  currentUserLogin?: string,
) {
  const canonicalQuery = `${REPO_PULLS_DEFAULT_QUERY} ${cleanPath.qualifier}:${cleanPath.value}`
  return (
    cleanPath.canonical &&
    currentUserLogin?.toLowerCase() === cleanPath.value.toLowerCase() &&
    normalizeRepoPullsQuery(query) === normalizeRepoPullsQuery(canonicalQuery)
  )
}

export function getSelectedRepoPullsSidebarLink(
  query: string,
  pathname: string,
  sidebarLinksParams: RepoPullsSidebarLinksParams,
) {
  const links = getRepoPullsSidebarLinks(sidebarLinksParams)
  const {currentUserLogin} = sidebarLinksParams
  const cleanPath = getRepoPullsCleanPath(pathname)
  if (cleanPath) {
    return isCanonicalRepoPullsCleanPathQuery(query, cleanPath, currentUserLogin)
      ? links.find(link => link.id === cleanPath.sidebarLinkId)
      : undefined
  }

  return links[0]
}

export function getRepoPullsAnalyticsView(
  query: string,
  pathname: string,
  sidebarLinksParams: RepoPullsSidebarLinksParams,
): RepoPullsAnalyticsView {
  const links = getRepoPullsSidebarLinks(sidebarLinksParams)
  const {currentUserLogin} = sidebarLinksParams
  const cleanPath = getRepoPullsCleanPath(pathname)
  if (cleanPath) {
    return isCanonicalRepoPullsCleanPathQuery(query, cleanPath, currentUserLogin) ? cleanPath.sidebarLinkId : 'custom'
  }

  return getRepoPullsSidebarLinkForQuery(query, links)?.id ?? 'custom'
}
